import React, { useCallback, useMemo } from "react";
import getSignedUrlAzureV2 from "src/main_app/services/apis/devices-download-file-azure-v2";
import { saveAs } from "file-saver";
import "./style.scss";

const CHART_HEIGHT = 30;
const BAR_WIDTH = 7;
const BAR_GAP = 1;
const CHART_PADDING = 10;
const BAR_COLOR = "#0BA2B4";

/**
 * Lightweight replacement for the recharts <ResponsiveContainer><BarChart/>
 * that used to be rendered once per row. With 50 rows on screen recharts had
 * to mount 50 ResizeObservers and thousands of SVG nodes before first paint.
 * Here every row is a single <svg> holding one background rect and one <path>
 * containing all of the bars, so the whole column costs ~100 DOM nodes.
 */
const UsageSparkline = React.memo(({ data }) => {
  const chart = useMemo(() => {
    const points = Array.isArray(data) ? data : [];
    if (!points.length) return null;

    let max = 0;
    for (let i = 0; i < points.length; i++) {
      const value = Number(points[i] && points[i].consumption) || 0;
      if (value > max) max = value;
    }

    const step = BAR_WIDTH + BAR_GAP;
    const scale = max > 0 ? CHART_HEIGHT / max : 0;
    let path = "";

    for (let i = 0; i < points.length; i++) {
      const value = Number(points[i] && points[i].consumption) || 0;
      if (value <= 0) continue;
      const height = Math.max(1, value * scale);
      const x = CHART_PADDING + i * step;
      const y = CHART_HEIGHT - height;
      path += `M${x} ${y}h${BAR_WIDTH}v${height}h-${BAR_WIDTH}z`;
    }

    return { path, width: points.length * step + CHART_PADDING * 2 };
  }, [data]);

  if (!chart) return null;

  return (
    <svg
      className="usage-sparkline"
      width="67%"
      height={CHART_HEIGHT}
      viewBox={`0 0 ${chart.width} ${CHART_HEIGHT}`}
      preserveAspectRatio="none"
      shapeRendering="crispEdges"
      focusable="false"
      aria-hidden="true"
    >
      <rect width={chart.width} height={CHART_HEIGHT} fill="#ffffff" />
      {chart.path ? <path d={chart.path} fill={BAR_COLOR} /> : null}
    </svg>
  );
});

UsageSparkline.displayName = "UsageSparkline";

const displaySeverity = (title, type) => (
  <span className={`alert-severity alert-severity--${type}`}>{title}</span>
);

const DeviceItem = ({ tenantConfig, titleHunt, device, date }) => {
  const downloadFile = useCallback(async () => {
    const response = await getSignedUrlAzureV2(date, device.meter_id);
    const disposition = response?.headers?.["content-disposition"] || "";
    const matchedFileName = disposition.match(/filename="([^"]+)"/i);
    const fileName = matchedFileName?.[1] || `device_data_${date}.csv`;
    const csvBlob = new Blob([response.data], {
      type: "text/csv;charset=utf-8",
    });
    saveAs(csvBlob, fileName);
  }, [date, device.meter_id]);

  //check if tenant is ICON to check with device.signal_strength else show the current return
  const shouldRenderDevice = () => {
    if (tenantConfig.title_hunt === "ICOT") {
      return true;
    }
    return device.scalar_value;
  };

  if (!shouldRenderDevice()) {
    return (
      <tr>
        <td className="statusError">
          {titleHunt === "HUNT" ? device.uuid : device.name}
        </td>
        <td>{device.meter_id}</td>
        <td>{titleHunt}</td>
        <td>""kL</td>
        <td></td>
        <td></td>
        <td>{displaySeverity("Not Processed", "warning")}</td>
      </tr>
    );
  }

  return (
    <tr className="device-item">
      <td>{titleHunt === "HUNT" ? device.uuid : device.name}</td>
      <td>{device.meter_id}</td>
      <td>{titleHunt}</td>
      <td>{device.scalar_value ? `${device.scalar_value}kL` : ""}</td>
      {/* <td>{Math.round(device.total_usage * 100) / 100}kL</td> */}
      <td>{device.signal_strength}</td>
      <td>{displaySeverity("On Time", "low")}</td>
      <td onClick={downloadFile}>{displaySeverity("File", "file")}</td>
      <td>
        <UsageSparkline data={device.usage_data} />
      </td>
      <td>{displaySeverity("Processed", "low")}</td>
    </tr>
  );
};

export default React.memo(DeviceItem);
